import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  InputAdornment,
  CircularProgress,
  Chip,
  Button,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import ClearIcon from '@mui/icons-material/Clear';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import PDFViewer from './PDFViewer';

// ─── helpers ────────────────────────────────────────────────────────────────

const paperLabel = (paper) => paper.title || paper.file_name || 'Untitled Paper';

const INITIAL_STATUS = 'Analyzing Papers';

const parseSseBuffer = (buffer) => {
  const events = [];
  let rest = buffer;
  while (true) {
    const idx = rest.indexOf('\n\n');
    if (idx === -1) break;
    const raw = rest.slice(0, idx);
    rest = rest.slice(idx + 2);
    let event = 'message';
    const dataLines = [];
    for (const line of raw.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim();
      else if (line.startsWith('data:')) dataLines.push(line.slice(5).trimStart());
    }
    if (!dataLines.length) continue;
    try {
      events.push({ event, data: JSON.parse(dataLines.join('\n')) });
    } catch {
      // skip malformed chunks
    }
  }
  return { events, rest };
};

const lastAssistantPapers = (msgs) => {
  for (let i = msgs.length - 1; i >= 0; i--) {
    if (msgs[i].role === 'assistant' && msgs[i].papers && msgs[i].papers.length > 0) {
      return msgs[i].papers;
    }
  }
  return null;
};

// ─── component ───────────────────────────────────────────────────────────────

const AISearchView = ({ apiBaseUrl }) => {
  const [messages, setMessages] = useState([]);
  const [sessionRid, setSessionRid] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [citationPapers, setCitationPapers] = useState(null);
  const [citationIndex, setCitationIndex] = useState(0);

  const openCitations = (papers) => {
    setCitationPapers(papers);
    setCitationIndex(0);
  };

  const closeCitations = () => setCitationPapers(null);

  // ── session ────────────────────────────────────────────────────────────────

  const handleNewChat = () => {
    setMessages([]);
    setSessionRid(null);
    closeCitations();
    setInput('');
  };

  // ── send ───────────────────────────────────────────────────────────────────

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const assistantId = crypto.randomUUID();
    let priorPapers = [];
    setMessages(m => {
      priorPapers = lastAssistantPapers(m) ?? [];
      return [...m,
        { id: crypto.randomUUID(), role: 'user', text },
        { id: assistantId, role: 'assistant', text: '', papers: [], status: INITIAL_STATUS },
      ];
    });
    setInput('');
    setLoading(true);

    const patchAssistant = (fields) => {
      setMessages(m => m.map(msg => (msg.id === assistantId ? { ...msg, ...fields } : msg)));
    };

    try {
      const res = await fetch(`${apiBaseUrl}/api/search/llm-search`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
        body: JSON.stringify({ query: text, limit: 100, session_rid: sessionRid }),
      });
      if (!res.ok || !res.body) throw new Error('request failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let answer = '';
      let status = INITIAL_STATUS;
      let papers = [];
      let sawTool = false;

      const flush = () => patchAssistant({ text: answer, status, papers });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseBuffer(buffer);
        buffer = parsed.rest;
        for (const { event, data } of parsed.events) {
          if (event === 'status' && data.message) {
            if (answer) answer = '';
            sawTool = true;
            status = data.message;
            flush();
          } else if (event === 'token' && data.delta) {
            answer += data.delta;
            if (!sawTool) status = null;
            flush();
          } else if (event === 'end') {
            if (data.session_rid) setSessionRid(data.session_rid);
            papers = (data.papers && data.papers.length > 0) ? data.papers : priorPapers;
            if (!answer) answer = data.agent_response || 'No response.';
            status = null;
            flush();
          } else if (event === 'error') {
            if (!answer) answer = data.message || 'Sorry, I encountered an error. Please try again.';
            status = null;
            flush();
          }
        }
      }
      if (status) {
        if (!answer) answer = 'No response.';
        status = null;
        flush();
      }
    } catch {
      setMessages(m => m.map(msg => (
        msg.id === assistantId
          ? {
              ...msg,
              text: 'Sorry, I encountered an error. Please try again.',
              status: null,
              papers: priorPapers,
            }
          : msg
      )));
    } finally {
      setLoading(false);
    }
  };

  // ── markdown rendering ─────────────────────────────────────────────────────

  const parseCitationLinks = (text) =>
    text.replace(/\[CITE:\d+:[^\]]+\]/g, '');

  const renderLineWithMarkdown = (lineText) => {
    const cleanedText = parseCitationLinks(lineText);
    const boldParts = cleanedText.split(/(\*\*[^*]+?\*\*)/g);
    return boldParts.map((boldPart, i) => {
      if (boldPart.match(/^\*\*[^*]+?\*\*$/)) {
        return <strong key={`bold-${i}`} style={{ fontWeight: 700, color: '#1e293b' }}>{boldPart.slice(2, -2)}</strong>;
      }
      const italicParts = boldPart.split(/(\*[^*]+?\*)/g);
      return italicParts.map((italicPart, j) => {
        if (italicPart.match(/^\*[^*]+?\*$/)) {
          return <em key={`italic-${i}-${j}`} style={{ fontStyle: 'italic', color: '#64748b' }}>{italicPart.slice(1, -1)}</em>;
        }
        return italicPart;
      });
    });
  };

  const renderTable = (tableLines) => {
    if (tableLines.length < 2) return null;
    const headerRow = tableLines[0].split('|').map(c => c.trim()).filter(Boolean);
    const rows = tableLines.slice(2).map(line =>
      line.split('|').map(c => c.trim()).filter(Boolean)
    );
    return (
      <Box sx={{ overflowX: 'auto', mb: 3, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
          <thead>
            <tr>
              {headerRow.map((h, i) => (
                <th key={i} style={{ textAlign: 'left', padding: '14px 16px', fontWeight: 700, color: '#1e293b', borderBottom: '2px solid #cbd5e1', backgroundColor: '#f8fafc' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f9fafb' }}>
                {row.map((cell, j) => (
                  <td key={j} style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', color: '#475569', lineHeight: 1.6 }}>
                    {renderLineWithMarkdown(cell)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    );
  };

  const renderTextWithMarkdown = (text) => {
    const lines = text.split('\n');
    const elements = [];
    let currentParagraph = [];
    let currentList = [];
    let currentTable = [];
    let inTable = false;

    const flushParagraph = () => {
      if (currentParagraph.length > 0) {
        elements.push(
          <Typography key={`p-${elements.length}`} variant="body1" sx={{ lineHeight: 1.7, mb: 1.5, color: '#475569', fontSize: '1rem' }}>
            {currentParagraph.map((pLine, i) => (
              <React.Fragment key={i}>
                {renderLineWithMarkdown(pLine)}
                {i < currentParagraph.length - 1 && ' '}
              </React.Fragment>
            ))}
          </Typography>
        );
        currentParagraph = [];
      }
    };

    const flushList = () => {
      if (currentList.length > 0) {
        elements.push(
          <Box key={`list-${elements.length}`} component="ul" sx={{ pl: 3, mb: 1.5, mt: 0.5, listStyleType: 'disc', '& li': { mb: 0.5, lineHeight: 1.6, color: '#475569', fontSize: '1rem', '&::marker': { color: '#2563eb' } } }}>
            {currentList.map((item, i) => (
              <li key={i}>
                <Typography variant="body1" component="span" sx={{ color: '#475569' }}>
                  {renderLineWithMarkdown(item)}
                </Typography>
              </li>
            ))}
          </Box>
        );
        currentList = [];
      }
    };

    const flushTable = () => {
      if (currentTable.length > 0) {
        elements.push(<React.Fragment key={`table-${elements.length}`}>{renderTable(currentTable)}</React.Fragment>);
        currentTable = [];
      }
    };

    lines.forEach((line, idx) => {
      const trimmed = line.trim();

      if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
        flushParagraph(); flushList(); flushTable();
        elements.push(<Box key={`sep-${idx}`} sx={{ height: '1px', bgcolor: '#e5e7eb', my: 4, mx: 'auto', width: '80%', borderRadius: '1px' }} />);
        return;
      }

      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        flushParagraph(); flushList();
        inTable = true;
        currentTable.push(line);
        return;
      } else if (inTable) {
        flushTable();
        inTable = false;
      }

      if (trimmed.startsWith('## ')) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`h2-${idx}`} variant="h5" sx={{ fontWeight: 700, color: '#1e293b', mt: 4, mb: 2, fontSize: '1.5rem', letterSpacing: '-0.02em', borderBottom: '2px solid #e5e7eb', pb: 1 }}>
            {trimmed.replace('## ', '')}
          </Typography>
        );
        return;
      }

      if (trimmed.startsWith('### ')) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`h3-${idx}`} variant="h6" sx={{ fontWeight: 700, color: '#2563eb', mt: 3, mb: 1.5, fontSize: '1.15rem', letterSpacing: '-0.02em' }}>
            {trimmed.replace('### ', '')}
          </Typography>
        );
        return;
      }

      if (trimmed.match(/^[-*]\s+/) || trimmed.match(/^\d+\.\s+/)) {
        flushParagraph();
        currentList.push(trimmed.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, ''));
        return;
      }

      if (trimmed.match(/^(Title|Authors|Summary|Reference|Citation|Key Researchers and Institutions|Area of Expertise|Region):?\s*$/i)) {
        flushParagraph(); flushList();
        elements.push(
          <Typography key={`label-${idx}`} variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', mt: 2, mb: 0.25, fontSize: '1.05rem', textTransform: 'uppercase', letterSpacing: '0.05em', opacity: 0.9 }}>
            {trimmed}
          </Typography>
        );
        return;
      }

      if (trimmed === '') {
        flushParagraph(); flushList();
        return;
      }

      flushList();
      currentParagraph.push(line);
    });

    flushParagraph(); flushList(); flushTable();
    return elements;
  };

  // ── sub-components ─────────────────────────────────────────────────────────

  const UserBubble = ({ message }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          px: 2.5,
          py: 1.5,
          maxWidth: '70%',
          bgcolor: '#2563eb',
          color: 'white',
          borderRadius: '18px 18px 4px 18px',
          boxShadow: '0 2px 8px rgba(37,99,235,0.25)',
        }}
      >
        <Typography variant="body1" sx={{ color: 'white', lineHeight: 1.6, fontSize: '1rem' }}>
          {message.text}
        </Typography>
      </Paper>
    </Box>
  );

  const AssistantBubble = ({ message }) => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
      <Paper
        elevation={0}
        sx={{
          px: 3,
          py: 2.5,
          maxWidth: '85%',
          bgcolor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '4px 18px 18px 18px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        }}
      >
        {message.status && !message.text && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={16} sx={{ color: '#2563eb' }} />
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              {message.status}
            </Typography>
          </Box>
        )}
        {message.text ? renderTextWithMarkdown(message.text) : null}
        {message.papers && message.papers.length > 0 && (
          <Paper
            elevation={0}
            onClick={() => openCitations(message.papers)}
            sx={{
              mt: 2.5,
              p: 1.75,
              bgcolor: '#f0f9ff',
              border: '1px solid #60a5fa',
              borderLeft: '3px solid #2563eb',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: '#dbeafe',
                borderColor: '#3b82f6',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <MenuBookIcon sx={{ color: '#2563eb' }} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.9rem' }}>
                  View Citations
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                  {message.papers.length} {message.papers.length === 1 ? 'paper' : 'papers'}
                </Typography>
              </Box>
            </Box>
          </Paper>
        )}
      </Paper>
    </Box>
  );

  // ── example questions ──────────────────────────────────────────────────────

  const exampleQuestions = [
    'Papers about GaN power amplifiers from 2015',
    'Recent advances in MOCVD technology',
    'Who are the leading researchers in compound semiconductors?',
    'Summarize packaging trends in wireless devices',
  ];

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 180px)', minHeight: '640px' }}>

      {/* Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1.5,
        borderBottom: '1px solid #e5e7eb',
        bgcolor: '#f8fafc',
        borderRadius: '12px 12px 0 0',
        flexShrink: 0,
      }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', fontSize: '1rem' }}>
          AI Research Assistant
        </Typography>
        <Button
          startIcon={<RestartAltIcon />}
          onClick={handleNewChat}
          size="small"
          variant="outlined"
          disabled={messages.length === 0 && !sessionRid}
          sx={{
            borderColor: '#e5e7eb',
            color: '#64748b',
            borderRadius: '8px',
            textTransform: 'none',
            fontSize: '0.85rem',
            '&:hover': { borderColor: '#2563eb', color: '#2563eb', bgcolor: '#eff6ff' },
            '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' },
          }}
        >
          New Chat
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: citationPapers ? '1 1 42%' : '1 1 100%',
          minWidth: 0,
          transition: 'flex-basis 0.2s ease',
        }}>
      {/* Message list */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        px: 3,
        py: 2,
        bgcolor: '#f8fafc',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': { background: '#cbd5e1', borderRadius: '3px', '&:hover': { background: '#94a3b8' } },
      }}>

        {/* Empty state */}
        {messages.length === 0 && !loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 3 }}>
            <Typography variant="h6" sx={{ color: '#94a3b8', fontWeight: 600 }}>
              Ask a question to get started
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, justifyContent: 'center', maxWidth: '600px' }}>
              {exampleQuestions.map((q, i) => (
                <Chip
                  key={i}
                  label={q}
                  size="medium"
                  onClick={() => setInput(q)}
                  sx={{
                    cursor: 'pointer',
                    bgcolor: '#ffffff',
                    color: '#475569',
                    fontWeight: 500,
                    borderRadius: '10px',
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: '#eff6ff', color: '#2563eb', borderColor: '#2563eb', transform: 'translateY(-2px)', boxShadow: '0 4px 8px rgba(37,99,235,0.15)' },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Messages */}
        {messages.map(msg =>
          msg.role === 'user'
            ? <UserBubble key={msg.id} message={msg} />
            : <AssistantBubble key={msg.id} message={msg} />
        )}
      </Box>

      {/* Composer */}
      <Box sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderTop: '1px solid #e5e7eb',
        bgcolor: '#ffffff',
        borderRadius: citationPapers ? 0 : '0 0 12px 12px',
        flexShrink: 0,
      }}>
        <TextField
          fullWidth
          multiline
          maxRows={4}
          placeholder="Ask about conference proceedings, authors, topics, or research trends..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#f8fafc',
              borderRadius: '12px',
              fontSize: '1rem',
              transition: 'all 0.2s ease',
              '& fieldset': { borderColor: '#e5e7eb' },
              '&:hover': { bgcolor: '#ffffff', '& fieldset': { borderColor: '#2563eb' } },
              '&.Mui-focused': { bgcolor: '#ffffff', '& fieldset': { borderColor: '#2563eb', borderWidth: '2px' } },
            },
            '& .MuiOutlinedInput-input': {
              padding: '12px 14px',
              '&::placeholder': { color: '#94a3b8', opacity: 1 },
            },
          }}
          InputProps={{
            endAdornment: input && (
              <InputAdornment position="end">
                <IconButton onClick={() => setInput('')} size="small" sx={{ color: '#94a3b8', '&:hover': { color: '#2563eb' } }}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || loading}
          sx={{
            bgcolor: '#2563eb',
            color: 'white',
            width: 48,
            height: 48,
            borderRadius: '12px',
            alignSelf: 'flex-end',
            boxShadow: '0 4px 12px rgba(37,99,235,0.3)',
            flexShrink: 0,
            '&:hover': { bgcolor: '#1d4ed8', boxShadow: '0 6px 16px rgba(37,99,235,0.4)', transform: 'translateY(-1px)' },
            '&.Mui-disabled': { bgcolor: '#e5e7eb', color: '#cbd5e1', boxShadow: 'none' },
            transition: 'all 0.2s ease',
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon fontSize="small" />}
        </IconButton>
      </Box>
        </Box>

      {citationPapers && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 58%',
          minWidth: 0,
          borderLeft: '1px solid #e5e7eb',
          bgcolor: '#ffffff',
          borderRadius: '0 0 12px 0',
        }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          px: 1.5,
          py: 1,
          borderBottom: '1px solid #e5e7eb',
          gap: 0.5,
          flexShrink: 0,
        }}>
          <IconButton size="small" onClick={closeCitations} aria-label="Close citations">
            <CloseIcon />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b', flex: 1, fontSize: '0.95rem' }}>
            Citations
          </Typography>
          {citationPapers.length > 0 && (
            <>
              <IconButton
                size="small"
                onClick={() => setCitationIndex((i) => Math.max(0, i - 1))}
                disabled={citationIndex <= 0}
                aria-label="Previous paper"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <Typography variant="caption" sx={{ color: '#64748b', minWidth: 48, textAlign: 'center' }}>
                {citationIndex + 1} / {citationPapers.length}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setCitationIndex((i) => Math.min(citationPapers.length - 1, i + 1))}
                disabled={citationIndex >= citationPapers.length - 1}
                aria-label="Next paper"
              >
                <NavigateNextIcon />
              </IconButton>
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          <Box sx={{
            width: 200,
            flexShrink: 0,
            overflowY: 'auto',
            borderRight: '1px solid #e5e7eb',
            p: 1,
            bgcolor: '#f8fafc',
          }}>
            {citationPapers.map((paper, i) => (
              <Box
                key={paper.cs_paper_id}
                onClick={() => setCitationIndex(i)}
                sx={{
                  p: 1,
                  mb: 0.75,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  bgcolor: i === citationIndex ? '#dbeafe' : '#ffffff',
                  border: i === citationIndex ? '1px solid #2563eb' : '1px solid #e5e7eb',
                  '&:hover': { bgcolor: i === citationIndex ? '#dbeafe' : '#eff6ff' },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.8rem', lineHeight: 1.35 }}>
                  {paperLabel(paper)}
                </Typography>
                {paper.year && (
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    {paper.year}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {citationPapers[citationIndex] && (
              <PDFViewer
                key={citationPapers[citationIndex].cs_paper_id}
                paperId={citationPapers[citationIndex].cs_paper_id}
                paperTitle={paperLabel(citationPapers[citationIndex])}
                apiBaseUrl={apiBaseUrl}
                source="ai_search"
              />
            )}
          </Box>
        </Box>
        </Box>
      )}
      </Box>

    </Box>
  );
};

export default AISearchView;
